import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Order.css";

const ORDER_API = "https://salathai.co.tz/mrms/Orders.php";
const MENU_API = "https://salathai.co.tz/mrms/Menu.php";

export default function Orders() {
    const token = localStorage.getItem("token");
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dateFilter, setDateFilter] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        filterType: 'today'
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editTotal, setEditTotal] = useState("");
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (!token) {
            Swal.fire("Session Expired", "Please login again", "warning");
            return;
        }
        loadAllData();
    }, [token]);

    useEffect(() => {
        filterOrdersByDate();
    }, [orders, dateFilter]);

    const loadAllData = async () => {
        setLoading(true);
        try {
            const orderRes = await fetch(`${ORDER_API}?admin=true`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const orderData = await orderRes.json();
            
            if (Array.isArray(orderData)) {
                const ordersWithItems = await Promise.all(
                    orderData.map(async (order) => {
                        try {
                            const detailRes = await fetch(`${ORDER_API}?id=${order.id}`, {
                                headers: { "Authorization": `Bearer ${token}` }
                            });
                            const detail = await detailRes.json();
                            
                            return {
                                id: order.id,
                                order_number: order.order_number,
                                total_amount: order.total_amount || 0,
                                created_at: order.created_at,
                                staff_name: order.staff_name || "Unknown",
                                payment_status: order.payment_status,
                                items: detail.items || [],
                                item_count: detail.items?.length || 0
                            };
                        } catch {
                            return { 
                                ...order, 
                                items: [], 
                                item_count: 0 
                            };
                        }
                    })
                );
                setOrders(ordersWithItems);
            } else {
                Swal.fire("Error", "Failed to load orders", "error");
            }
        } catch (err) {
            console.error("Load error:", err);
            Swal.fire("Error", "Server not reachable", "error");
        } finally {
            setLoading(false);
        }
    };

    const filterOrdersByDate = () => {
        let filtered = [...orders];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch(dateFilter.filterType) {
            case 'today':
                filtered = orders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    orderDate.setHours(0, 0, 0, 0);
                    return orderDate.getTime() === today.getTime();
                });
                break;
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                filtered = orders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    orderDate.setHours(0, 0, 0, 0);
                    return orderDate.getTime() === yesterday.getTime();
                });
                break;
            case 'thisWeek':
                const weekStart = new Date(today);
                weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);
                filtered = orders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    return orderDate >= weekStart && orderDate <= weekEnd;
                });
                break;
            case 'custom':
                if (dateFilter.startDate && dateFilter.endDate) {
                    const start = new Date(dateFilter.startDate);
                    start.setHours(0, 0, 0, 0);
                    const end = new Date(dateFilter.endDate);
                    end.setHours(23, 59, 59, 999);
                    filtered = orders.filter(order => {
                        const orderDate = new Date(order.created_at);
                        return orderDate >= start && orderDate <= end;
                    });
                }
                break;
            default:
                filtered = orders;
        }

        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setFilteredOrders(filtered);
    };

    const handleFilterChange = (type) => {
        setDateFilter(prev => ({ ...prev, filterType: type }));
    };

    const viewOrder = (order) => {
        setSelectedOrder(order);
        setEditTotal(order.total_amount);
        setIsEditing(false);
        setShowModal(true);
    };

    // ==================== UPDATE ORDER TOTAL ====================
    const updateOrderTotal = async () => {
        if (!editTotal || Number(editTotal) < 0) {
            Swal.fire("Invalid Amount", "Please enter a valid total amount", "warning");
            return;
        }

        const result = await Swal.fire({
            title: "Update Order Total?",
            html: `Change total from <strong>${formatCurrency(selectedOrder.total_amount)}</strong> to <strong>${formatCurrency(editTotal)}</strong>?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3b82f6",
            confirmButtonText: "Yes, update it"
        });

        if (!result.isConfirmed) return;

        setProcessing(true);
        try {
            const res = await fetch(`${ORDER_API}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    id: selectedOrder.id,
                    total_amount: Number(editTotal)
                })
            });

            const data = await res.json();
            
            if (data.success) {
                Swal.fire("Updated!", "Order total has been updated.", "success");
                setIsEditing(false);
                setOrders(prev => prev.map(o => 
                    o.id === selectedOrder.id 
                        ? { ...o, total_amount: Number(editTotal) } 
                        : o
                ));
                setSelectedOrder(prev => ({ ...prev, total_amount: Number(editTotal) }));
            } else {
                Swal.fire("Error", data.error || "Failed to update", "error");
            }
        } catch (err) {
            Swal.fire("Error", "Network error occurred", "error");
        } finally {
            setProcessing(false);
        }
    };

    // ==================== DELETE ORDER ITEM ====================
    const deleteOrderItem = async (item) => {
        const result = await Swal.fire({
            title: "Delete Item?",
            html: `Are you sure you want to delete <strong>${item.item_name}</strong> (x${item.quantity})?<br/><small>This will recalculate the order total.</small>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete it",
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        setProcessing(true);
        try {
            const res = await fetch(`${ORDER_API}?id=${selectedOrder.id}&action=delete_item&item_id=${item.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await res.json();
            
            if (data.success) {
                if (data.order_deleted) {
                    Swal.fire("Deleted!", "Order deleted (no items remaining).", "success");
                    setShowModal(false);
                    loadAllData();
                } else {
                    Swal.fire("Deleted!", "Item has been removed and total recalculated.", "success");
                    const detailRes = await fetch(`${ORDER_API}?id=${selectedOrder.id}`, {
                        headers: { "Authorization": `Bearer ${token}` }
                    });
                    const detail = await detailRes.json();
                    
                    setSelectedOrder(prev => ({
                        ...prev,
                        items: detail.items || [],
                        total_amount: data.new_order_total || 0,
                        item_count: detail.items?.length || 0
                    }));
                    
                    setOrders(prev => prev.map(o => 
                        o.id === selectedOrder.id 
                            ? { ...o, total_amount: data.new_order_total, items: detail.items, item_count: detail.items?.length || 0 }
                            : o
                    ));
                }
            } else {
                Swal.fire("Error", data.error || "Failed to delete item", "error");
            }
        } catch (err) {
            Swal.fire("Error", "Network error occurred", "error");
        } finally {
            setProcessing(false);
        }
    };

    // ==================== UPDATE ITEM QUANTITY ====================
    const updateItemQuantity = async (item, newQty) => {
        if (newQty < 1) {
            Swal.fire("Invalid Quantity", "Quantity must be at least 1", "warning");
            return;
        }

        setProcessing(true);
        try {
            const res = await fetch(`${ORDER_API}?id=${selectedOrder.id}&action=update_item`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    item_id: item.id,
                    quantity: newQty
                })
            });

            const data = await res.json();
            
            if (data.success) {
                Swal.fire({
                    title: "Updated!",
                    text: "Item quantity updated.",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
                
                const detailRes = await fetch(`${ORDER_API}?id=${selectedOrder.id}`, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                const detail = await detailRes.json();
                
                setSelectedOrder(prev => ({
                    ...prev,
                    items: detail.items || [],
                    total_amount: data.new_order_total || 0,
                    item_count: detail.items?.length || 0
                }));
                
                setOrders(prev => prev.map(o => 
                    o.id === selectedOrder.id 
                        ? { ...o, total_amount: data.new_order_total, items: detail.items, item_count: detail.items?.length || 0 }
                        : o
                ));
            } else {
                Swal.fire("Error", data.error || "Failed to update item", "error");
            }
        } catch (err) {
            Swal.fire("Error", "Network error occurred", "error");
        } finally {
            setProcessing(false);
        }
    };

    // ==================== DELETE ENTIRE ORDER ====================
    const deleteEntireOrder = async () => {
        const result = await Swal.fire({
            title: "Delete Order?",
            html: `Are you sure you want to delete order <strong>#${selectedOrder.order_number}</strong>?<br/><small>This action cannot be undone.</small>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete order",
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        setProcessing(true);
        try {
            const res = await fetch(`${ORDER_API}?id=${selectedOrder.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await res.json();
            
            if (data.success) {
                Swal.fire("Deleted!", "Order has been deleted.", "success");
                setShowModal(false);
                loadAllData();
            } else {
                Swal.fire("Error", data.error || "Failed to delete order", "error");
            }
        } catch (err) {
            Swal.fire("Error", "Network error occurred", "error");
        } finally {
            setProcessing(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatCurrency = (amount) => {
        return Number(amount || 0).toLocaleString() + ' TZS';
    };

    const calculateTotals = () => {
        const totalAmount = filteredOrders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
        return { totalAmount };
    };

    const totals = calculateTotals();

    const filterStyles = {
        container: {
            background: 'linear-gradient(135deg, #033349 0%, #556b04 100%)',
            padding: '20px',
            borderRadius: '15px',
            marginBottom: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        },
        title: {
            color: 'white',
            margin: '0 0 15px 0',
            fontSize: '1.2rem',
            fontWeight: '600'
        },
        buttonGroup: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '15px'
        },
        filterButton: (isActive) => ({
            padding: '10px 20px',
            border: 'none',
            borderRadius: '25px',
            fontWeight: '600',
            cursor: 'pointer',
            background: isActive ? 'white' : 'rgba(255,255,255,0.2)',
            color: isActive ? '#764ba2' : 'white',
            transition: 'all 0.3s ease',
            boxShadow: isActive ? '0 5px 15px rgba(0,0,0,0.2)' : 'none'
        }),
        dateRange: {
            display: 'flex',
            gap: '15px',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: 'rgba(255,255,255,0.1)',
            padding: '15px',
            borderRadius: '10px'
        },
        dateInput: {
            padding: '8px 15px',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '8px',
            background: 'white',
            color: '#333',
            fontSize: '0.9rem'
        },
        summary: {
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '15px',
            padding: '15px',
            marginTop: '15px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
        },
        summaryItem: {
            textAlign: 'center',
            padding: '10px',
            borderRight: '1px solid #e5e7eb'
        },
        summaryLabel: {
            fontSize: '0.9rem',
            color: '#6b7280',
            marginBottom: '5px'
        },
        summaryValue: {
            fontSize: '1.4rem',
            fontWeight: '700',
            color: '#374151'
        },
        summaryValueHighlight: {
            fontSize: '1.4rem',
            fontWeight: '700',
            color: '#059669'
        },
        actionButton: {
            padding: '6px 12px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'all 0.2s'
        }
    };

    if (loading) {
        return (
            <div className="om-container">
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: '20px'
                }}>
                    <div className="spinner" style={{
                        width: '50px',
                        height: '50px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="om-container">
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .filter-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
                }
                .action-btn:hover {
                    opacity: 0.9;
                    transform: scale(1.02);
                }
                .edit-input:focus {
                    outline: 2px solid #3b82f6;
                    border-color: #3b82f6;
                }
            `}</style>

            <div className="om-header">
                <h2>Orders Management</h2>
                <div className="om-header-actions">
                    <button onClick={loadAllData} className="om-refresh-btn">
                        Refresh Orders
                    </button>
                    <span className="om-total-count">
                        Total Orders: {orders.length}
                    </span>
                </div>
            </div>

            <div style={filterStyles.container}>
                <h3 style={filterStyles.title}>Filter Orders by Date</h3>
                
                <div style={filterStyles.buttonGroup}>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'today')}
                        onClick={() => handleFilterChange('today')}
                        className="filter-button"
                    >
                        Today
                    </button>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'yesterday')}
                        onClick={() => handleFilterChange('yesterday')}
                        className="filter-button"
                    >
                        Yesterday
                    </button>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'thisWeek')}
                        onClick={() => handleFilterChange('thisWeek')}
                        className="filter-button"
                    >
                        This Week
                    </button>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'custom')}
                        onClick={() => handleFilterChange('custom')}
                        className="filter-button"
                    >
                        Custom Range
                    </button>
                </div>

                {dateFilter.filterType === 'custom' && (
                    <div style={filterStyles.dateRange}>
                        <input
                            type="date"
                            value={dateFilter.startDate}
                            onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                            style={filterStyles.dateInput}
                        />
                        <span style={{ color: 'white' }}>to</span>
                        <input
                            type="date"
                            value={dateFilter.endDate}
                            onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                            style={filterStyles.dateInput}
                        />
                    </div>
                )}

                <div style={filterStyles.summary}>
                    <div style={filterStyles.summaryItem}>
                        <div style={filterStyles.summaryLabel}>Orders in Period</div>
                        <div style={filterStyles.summaryValue}>{filteredOrders.length}</div>
                    </div>
                    <div style={filterStyles.summaryItem}>
                        <div style={filterStyles.summaryLabel}>Total Sales</div>
                        <div style={filterStyles.summaryValueHighlight}>{formatCurrency(totals.totalAmount)}</div>
                    </div>
                </div>
            </div>

            {filteredOrders.length === 0 ? (
                <div className="om-empty">
                    <p>No orders found for selected date range</p>
                    <button onClick={loadAllData} className="om-load-btn">
                        Load All Orders
                    </button>
                </div>
            ) : (
                <div className="om-table-wrapper">
                    <table className="om-table">
                        <thead>
                            <tr>
                                <th>Order #</th>
                                <th>Items</th>
                                <th>Total</th>
                                <th>Created At</th>
                                <th>Staff</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map(order => (
                                <tr key={order.id}>
                                    <td className="om-order-number">
                                        <strong>#{order.order_number}</strong>
                                    </td>
                                    <td className="om-items-preview">
                                        {order.items?.slice(0, 2).map((item, idx) => (
                                            <div key={idx} className="om-item-preview">
                                                {item.item_name || "Unknown Item"} ×{item.quantity}
                                            </div>
                                        ))}
                                        {order.item_count > 2 && (
                                            <div className="om-more-items">
                                                +{order.item_count - 2} more items
                                            </div>
                                        )}
                                        {order.item_count === 0 && (
                                            <div className="om-no-items">No items</div>
                                        )}
                                    </td>
                                    <td className="om-total">
                                        {formatCurrency(order.total_amount)}
                                    </td>
                                    <td className="om-date">
                                        {formatDate(order.created_at)}
                                    </td>
                                    <td className="om-staff">
                                        {order.staff_name}
                                    </td>
                                    <td className="om-actions">
                                        <button 
                                            onClick={() => viewOrder(order)} 
                                            className="om-view-btn action-btn"
                                        >
                                            View / Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ==================== ORDER DETAILS MODAL ==================== */}
            {showModal && selectedOrder && (
                <div className="om-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="om-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: "800px" }}>
                        <div className="om-modal-header">
                            <h3>Order #{selectedOrder.order_number}</h3>
                            <button className="om-close-btn" onClick={() => setShowModal(false)}>
                                ×
                            </button>
                        </div>
                        
                        <div className="om-order-details">
                            <div className="om-detail-row">
                                <div className="om-detail-item">
                                    <strong>Staff:</strong>
                                    <span>{selectedOrder.staff_name}</span>
                                </div>
                                <div className="om-detail-item">
                                    <strong>Order Date:</strong>
                                    <span>{formatDate(selectedOrder.created_at)}</span>
                                </div>
                                <div className="om-detail-item">
                                    <strong>Total Items:</strong>
                                    <span>{selectedOrder.item_count}</span>
                                </div>
                            </div>
                            
                            {/* Editable Total Amount */}
                            <div style={{
                                background: "#f8fafc",
                                padding: "15px",
                                borderRadius: "10px",
                                marginTop: "15px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "10px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <strong>Total Amount:</strong>
                                    {isEditing ? (
                                        <input
                                            type="number"
                                            value={editTotal}
                                            onChange={(e) => setEditTotal(e.target.value)}
                                            className="edit-input"
                                            style={{
                                                padding: "8px 12px",
                                                border: "2px solid #e5e7eb",
                                                borderRadius: "8px",
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                width: "150px"
                                            }}
                                            min="0"
                                            step="1000"
                                        />
                                    ) : (
                                        <span style={{ fontSize: "20px", fontWeight: "700", color: "#059669" }}>
                                            {formatCurrency(selectedOrder.total_amount)}
                                        </span>
                                    )}
                                </div>
                                
                                <div style={{ display: "flex", gap: "8px" }}>
                                    {isEditing ? (
                                        <>
                                            <button
                                                onClick={updateOrderTotal}
                                                disabled={processing}
                                                style={{
                                                    ...filterStyles.actionButton,
                                                    background: "#3b82f6",
                                                    color: "white"
                                                }}
                                            >
                                                {processing ? "Saving..." : "✓ Save"}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsEditing(false);
                                                    setEditTotal(selectedOrder.total_amount);
                                                }}
                                                style={{
                                                    ...filterStyles.actionButton,
                                                    background: "#e5e7eb",
                                                    color: "#374151"
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            style={{
                                                ...filterStyles.actionButton,
                                                background: "#3b82f6",
                                                color: "white"
                                            }}
                                        >
                                            ✏️ Edit Total
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="om-items-section">
                            <h4>Order Items</h4>
                            {selectedOrder.items?.length > 0 ? (
                                <table className="om-items-table">
                                    <thead>
                                        <tr>
                                            <th>Item Name</th>
                                            <th>Quantity</th>
                                            <th>Unit Price</th>
                                            <th>Subtotal</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.items.map((item, i) => (
                                            <tr key={i}>
                                                <td>{item.item_name || "Unknown Item"}</td>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                        <button
                                                            onClick={() => updateItemQuantity(item, item.quantity - 1)}
                                                            disabled={processing || item.quantity <= 1}
                                                            style={{
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "50%",
                                                                border: "1px solid #e5e7eb",
                                                                background: "#f9fafb",
                                                                cursor: item.quantity <= 1 ? "not-allowed" : "pointer",
                                                                fontWeight: "bold",
                                                                color: "#374151"
                                                            }}
                                                        >
                                                            −
                                                        </button>
                                                        <span style={{ fontWeight: "600", minWidth: "30px", textAlign: "center" }}>
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            onClick={() => updateItemQuantity(item, item.quantity + 1)}
                                                            disabled={processing}
                                                            style={{
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "50%",
                                                                border: "1px solid #e5e7eb",
                                                                background: "#f9fafb",
                                                                cursor: "pointer",
                                                                fontWeight: "bold",
                                                                color: "#374151"
                                                            }}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>{formatCurrency(item.price)}</td>
                                                <td style={{ fontWeight: "600" }}>{formatCurrency(item.subtotal)}</td>
                                                <td>
                                                    <button
                                                        onClick={() => deleteOrderItem(item)}
                                                        disabled={processing}
                                                        style={{
                                                            ...filterStyles.actionButton,
                                                            background: "#fef2f2",
                                                            color: "#dc2626",
                                                            border: "1px solid #fecaca"
                                                        }}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="om-no-items-modal">
                                    No items found for this order
                                </div>
                            )}
                        </div>
                        
                        <div className="om-modal-footer" style={{ 
                            display: "flex", 
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "10px"
                        }}>
                            <button
                                onClick={deleteEntireOrder}
                                disabled={processing}
                                style={{
                                    ...filterStyles.actionButton,
                                    background: "#dc2626",
                                    color: "white",
                                    padding: "10px 20px"
                                }}
                            >
                                🗑️ Delete Entire Order
                            </button>
                            <button 
                                className="om-close-modal-btn" 
                                onClick={() => setShowModal(false)}
                                style={{ padding: "10px 20px" }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}